import React, { useRef, useEffect, useMemo, Suspense } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { useGLTF } from '@react-three/drei';
import * as THREE from 'three';
import { SkeletonUtils, MeshSurfaceSampler } from 'three-stdlib';

function MaleModel() {
  const { scene: originalScene } = useGLTF('/models/universe_character.glb');
  const clonedScene = useMemo(() => originalScene ? SkeletonUtils.clone(originalScene) : null, [originalScene]);
  const modelRef = useRef();
  const holderRef = useRef();
  const starsObj = useRef();
  const veinObj = useRef();

  const STAR_COUNT = 200;
  const inset = 0.01;
  const VEIN_COLOR = "#7ff4ed";
  const VEIN_INTENSITY = 1.3;
  const INSET_MM = 1.0;

  useFrame((state) => {
    if (modelRef.current) modelRef.current.rotation.y += 0.003;
    
    const t = state.clock.getElapsedTime();
    if (veinObj.current?.material?.uniforms?.uTime) {
      veinObj.current.material.uniforms.uTime.value = t;
      const fillCycle = (t % 15) / 15;
      if (veinObj.current.material.uniforms.uFillLevel) {
        veinObj.current.material.uniforms.uFillLevel.value = fillCycle;
      }
    }
  });

  const mainMesh = useMemo(() => {
    if (!clonedScene) return null;
    let foundMesh = null;
    clonedScene.traverse((child) => {
      if (child.isMesh && !foundMesh) foundMesh = child;
    });
    return foundMesh;
  }, [clonedScene]);

  useEffect(() => {
    if (!mainMesh || !holderRef.current) return;
    
    mainMesh.material = new THREE.MeshPhysicalMaterial({
      color: 0x5ee8e0,
      transparent: true,
      opacity: 0.35,
      transmission: 0.98,
      thickness: 0.15,
      roughness: 0.05,
      metalness: 0.0,
      ior: 1.2,
      attenuationColor: new THREE.Color(0x7ff4ed),
      attenuationDistance: 0.8,
      clearcoat: 0.8,
      clearcoatRoughness: 0.05,
      side: THREE.DoubleSide,
      emissive: new THREE.Color(0x2a9d97),
      emissiveIntensity: 0.15,
    });
    mainMesh.material.depthWrite = false;
    mainMesh.renderOrder = 1;
    
    const r = mainMesh.geometry.boundingSphere?.radius || 1;
    
    const veinVert = `
      varying vec3 vPos;
      uniform float uInset;
      void main(){
        vec3 displaced = position - normal * uInset;
        vec4 mvPosition = modelViewMatrix * vec4(displaced, 1.0);
        vPos = (modelMatrix * vec4(displaced,1.0)).xyz;
        gl_Position = projectionMatrix * mvPosition;
      }
    `;

    const veinFrag = `
      precision highp float;
      varying vec3 vPos;
      uniform float uTime;
      uniform vec3 uColor;
      uniform float uIntensity;
      uniform float uFillLevel;

      float hash(vec3 p){ return fract(sin(dot(p, vec3(127.1,311.7,74.7))) * 43758.5453123); }
      float noise(vec3 x){
        vec3 i=floor(x), f=fract(x);
        vec3 u=f*f*(3.0-2.0*f);
        return mix(mix(mix(hash(i+vec3(0,0,0)),hash(i+vec3(1,0,0)),u.x),
                       mix(hash(i+vec3(0,1,0)),hash(i+vec3(1,1,0)),u.x),u.y),
                   mix(mix(hash(i+vec3(0,0,1)),hash(i+vec3(1,0,1)),u.x),
                       mix(hash(i+vec3(0,1,1)),hash(i+vec3(1,1,1)),u.x),u.y),u.z);
      }

      void main(){
        float normalizedY = vPos.y;
        
        float wave = sin(vPos.x * 8.0 + uTime * 3.0) * cos(vPos.z * 8.0 + uTime * 2.5) * 0.08;
        float fillHeight = -2.0 + (uFillLevel * 4.0);
        float waterSurface = fillHeight + wave;
        
        float waterMask = step(normalizedY, waterSurface);
        
        float depthFactor = (waterSurface - normalizedY) / 2.0;
        vec3 deepColor = vec3(0.2, 0.55, 0.52);
        vec3 surfaceColor = vec3(0.5, 0.95, 0.92);
        vec3 waterColor = mix(surfaceColor, deepColor, clamp(depthFactor, 0.0, 1.0));
        
        float bubbleNoise = noise(vPos * 5.0 + vec3(0.0, uTime * 2.0, 0.0));
        float bubbles = smoothstep(0.85, 0.9, bubbleNoise) * waterMask;
        
        float surfaceGlow = smoothstep(0.05, 0.0, abs(normalizedY - waterSurface)) * 2.0;
        
        vec3 finalColor = waterColor * (0.8 + bubbles * 0.5) + vec3(surfaceGlow);
        float alpha = waterMask * (0.7 + bubbles * 0.3 + surfaceGlow * 0.5);
        
        gl_FragColor = vec4(finalColor, alpha);
      }
    `;

    const veinMaterial = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uColor: { value: new THREE.Color(VEIN_COLOR) },
        uIntensity: { value: VEIN_INTENSITY },
        uInset: { value: r * (INSET_MM / 1000) * 0.5 },
        uFillLevel: { value: 0 },
      },
      vertexShader: veinVert,
      fragmentShader: veinFrag,
      transparent: true,
      depthWrite: false,
      depthTest: false,
      blending: THREE.AdditiveBlending,
    });

    const veinMesh = new THREE.Mesh(mainMesh.geometry, veinMaterial);
    veinMesh.renderOrder = 2;
    veinObj.current = veinMesh;
    mainMesh.add(veinMesh);
    
    const sceneBbox = new THREE.Box3().setFromObject(clonedScene);
    const center = sceneBbox.getCenter(new THREE.Vector3());
    clonedScene.position.sub(center);
    
    holderRef.current.add(clonedScene);

    const sampler = new MeshSurfaceSampler(mainMesh).build();
    const pos = new Float32Array(STAR_COUNT * 3);
    const starBbox = new THREE.Box3().setFromObject(mainMesh);
    const p = new THREE.Vector3();
    const n = new THREE.Vector3();
    const minY = starBbox.min.y;
    const maxY = starBbox.max.y;
    const headStart = minY + (maxY - minY) * 0.8;

    let k = 0;
    for (let i = 0; i < STAR_COUNT; i++) {
      let accepted = false;
      for (let tries = 0; tries < 3 && !accepted; tries++) {
        sampler.sample(p, n);
        const bias = p.y > headStart ? 0.4 : 1.0;
        if (Math.random() < bias) accepted = true;
      }
      p.addScaledVector(n, -inset);
      pos[k * 3] = p.x; pos[k * 3 + 1] = p.y; pos[k * 3 + 2] = p.z; k++;
    }

    const starGeometry = new THREE.BufferGeometry();
    starGeometry.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    const starMaterial = new THREE.PointsMaterial({ 
      size: 0.018, 
      sizeAttenuation: true, 
      color: '#ffffff', 
      transparent: true, 
      opacity: 0.9, 
      depthWrite: false, 
      depthTest: true, 
      blending: THREE.AdditiveBlending 
    });

    const stars = new THREE.Points(starGeometry, starMaterial);
    stars.renderOrder = 3; 
    starsObj.current = stars; 
    mainMesh.add(stars);

    return () => { 
      if (starsObj.current) { 
        starsObj.current.material.dispose(); 
        starsObj.current.geometry.dispose(); 
      }
      if (veinObj.current) {
        mainMesh.remove(veinObj.current);
        veinObj.current.material.dispose();
        veinObj.current.geometry?.dispose?.();
        veinObj.current = null;
      }
    };
  }, [mainMesh]);

  if (!clonedScene) return null;
  return (
    <group ref={modelRef} scale={[1.5, 1.5, 1.5]} position={[0, 0, 0]}>
      <group ref={holderRef} />
    </group>
  );
}

useGLTF.preload('/models/universe_character.glb');

const NetworkMaleModel = ({ onClick }) => {
  return (
    <div 
      className="new-human-div network-3d-wrapper" 
      onClick={(e) => {
        e.stopPropagation();
        if (onClick) onClick();
      }}
      style={{ cursor: onClick ? 'pointer' : 'default', position: 'relative' }}
    >
      <Canvas 
        camera={{ position: [0, 0, 5.5], fov: 40 }} 
        style={{ 
          width: '141px', 
          height: '246px', 
          background: 'transparent',
          pointerEvents: 'none'
        }}
      >
        <Suspense fallback={null}>
          <ambientLight intensity={0.45} />
          <directionalLight position={[3, 3, 2]} intensity={0.9} />
          <pointLight position={[-10, -10, -10]} intensity={0.4} />
          <pointLight position={[10, 10, 10]} intensity={0.4} />
          <MaleModel />
        </Suspense>
      </Canvas>
    </div>
  );
};

export default NetworkMaleModel;
